// src/screens/AdvertiserScreen.js
// ============================================================================
// ADVERTISER PORTAL - Information for advertising and marketing partners
// 
// LEGAL COMPLIANCE: Uses custodial terminology per CUSTODIAL_TERMINOLOGY_GUIDE.md
// - Advertisers purchase BRANDING/AD PLACEMENTS, not prize pool contributions
// - "Tournament Branding" NOT "Tournament Sponsorship" (sponsorship implies funding)
// - Prize pools are 100% player-funded, advertisers don't contribute
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, ScrollView,
  TouchableOpacity, ActivityIndicator, Linking, Modal, TextInput
} from 'react-native';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../../firebaseConfig';

import { Ionicons } from '@expo/vector-icons';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useError } from '../components/ErrorContext';
import useMembershipsLive from '../hooks/useMembershipsLive';

// ============================================================================
// ADVERTISING PLACEMENTS
// Note: "Tournament Branding" not "Sponsorship" - advertisers buy ad space,
// they do NOT contribute to prize pools (which are 100% player-funded)
// ============================================================================
const AD_PLACEMENTS = [
  {
    type: 'Stream Pre-Roll',
    description: 'Video ads before match streams begin.',
    format: '15-30 second video',
    iconName: 'videocam',
    available: true,
  },
  {
    type: 'In-App Display',
    description: 'Banners on lobby, results, and bracket screens.',
    format: '320x50, 728x90, 300x250',
    iconName: 'phone-portrait',
    available: true,
  },
  {
    type: 'Tournament Branding',
    description: 'Your logo on tournament pages and brackets. Branding placement only.',
    format: 'Logo + banner assets',
    iconName: 'trophy',
    available: true,
  },
  {
    type: 'Stream Overlay',
    description: 'Branded overlays visible during live matches.',
    format: 'PNG/SVG assets',
    iconName: 'layers',
    available: true,
  },
];

// ============================================================================
// PARTNERSHIP TIERS
// ============================================================================
const PARTNERSHIP_TIERS = [
  {
    id: 'pilot',
    name: 'Pilot Campaign',
    price: 'From $1,000',
    description: 'Test the platform with a limited campaign.',
    features: [
      'Single placement type',
      'Basic reporting',
      'Direct team access',
    ],
    iconName: 'rocket',
    cta: 'Start Conversation',
  },
  {
    id: 'partner',
    name: 'Advertising Partner',
    price: 'Custom',
    description: 'Ongoing campaigns with premium placements.',
    features: [
      'Multi-placement campaigns',
      'Detailed analytics dashboard',
      'Dedicated account manager',
      'A/B testing support',
    ],
    highlight: true,
    iconName: 'trending-up',
    cta: 'Discuss Partnership',
  },
  {
    id: 'strategic',
    name: 'Strategic Partner',
    price: 'Custom',
    description: 'Deep integration and co-marketing opportunities.',
    features: [
      'Category exclusivity options',
      'Custom branded experiences',
      'Co-marketing opportunities',
      'Direct leadership access',
    ],
    iconName: 'star',
    cta: 'Explore Options',
  },
];

// "Paying Users" line: a compensation-source list, so it names channel
// memberships once they have ever launched (everLaunched, design 13.8).
// Team memberships (evergreen 3.5b, design 25.8): four states on (channel
// everLaunched, teamEverLaunched); (false, false) and (true, false) are the
// 3.5a lines unchanged.
export function payingUsersDescFor(everLaunched, teamEverLaunched = false) {
  if (teamEverLaunched) {
    return everLaunched
      ? 'Paid plans, channel and team memberships, Super Crits & Rumbles'
      : 'Paid plans, team memberships, Super Crits & Rumbles';
  }
  return everLaunched
    ? 'Paid plans, channel memberships, Super Crits & Rumbles'
    : 'Paid plans, Super Crits & Rumbles';
}

const EVENT_SPONSOR_LEVEL_OPTIONS = [
  { key: 'presenting', label: 'Presented by' },
  { key: 'featured', label: 'Featured sponsors' },
  { key: 'community', label: 'Community sponsors' },
  { key: 'prize', label: 'Additional prizes' },
  { key: 'not_sure', label: 'Not sure yet' },
];

export default function AdvertiserScreen({
  navigation,
  route,
  initialLoading = true,
  initialSponsorableEvents = null,
  initialSponsorForm = null,
  initialGeneralForm = null,
}) {
  const { everLaunched, teamEverLaunched } = useMembershipsLive();
  const { showError } = useError();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(initialLoading);
  const [submitting, setSubmitting] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  
  const [formData, setFormData] = useState(
    initialGeneralForm || {
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      budget: '',
      goals: '',
    }
  );

  const preselectedEventId = route?.params?.sponsorEventId
    ? String(route.params.sponsorEventId).trim()
    : '';
  const initialEventsList = Array.isArray(initialSponsorableEvents)
    ? initialSponsorableEvents
    : [];
  const initialPickedId =
    (preselectedEventId &&
    initialEventsList.some((e) => e.tournamentId === preselectedEventId)
      ? preselectedEventId
      : initialEventsList[0]?.tournamentId) ||
    preselectedEventId ||
    '';

  const [sponsorableEvents, setSponsorableEvents] = useState(initialEventsList);
  const [selectedEventId, setSelectedEventId] = useState(initialPickedId);
  const [sponsorSubmitting, setSponsorSubmitting] = useState(false);
  const [sponsorNotice, setSponsorNotice] = useState('');
  const [sponsorError, setSponsorError] = useState('');
  const [sponsorForm, setSponsorForm] = useState(
    initialSponsorForm || {
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      levelInterest: 'not_sure',
      message: '',
    }
  );

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return unsubAuth;
  }, []);

  useEffect(() => {
    if (Array.isArray(initialSponsorableEvents)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const functions = getFunctions();
        const listFn = httpsCallable(functions, 'listSponsorableEvents');
        const res = await listFn({});
        if (cancelled) return;
        const events = Array.isArray(res?.data?.events) ? res.data.events : [];
        setSponsorableEvents(events);
        if (events.length > 0) {
          const match = preselectedEventId
            ? events.find((e) => e.tournamentId === preselectedEventId)
            : null;
          setSelectedEventId((match || events[0]).tournamentId);
        }
      } catch (_) {
        if (!cancelled) setSponsorableEvents([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialSponsorableEvents, preselectedEventId]);

  useEffect(() => {
    if (
      preselectedEventId &&
      sponsorableEvents.some((e) => e.tournamentId === preselectedEventId)
    ) {
      setSelectedEventId(preselectedEventId);
    }
  }, [preselectedEventId, sponsorableEvents]);

  const handleSubmitEventSponsor = async () => {
    setSponsorNotice('');
    setSponsorError('');
    if (
      !selectedEventId ||
      !sponsorForm.companyName.trim() ||
      !sponsorForm.contactName.trim() ||
      !sponsorForm.email.trim()
    ) {
      const msg = 'Please fill in all required fields.';
      setSponsorError(msg);
      showError('Missing Information', msg);
      return;
    }

    setSponsorSubmitting(true);
    try {
      const functions = getFunctions();
      const submitAdInquiry = httpsCallable(functions, 'submitAdvertiserInquiry');
      await submitAdInquiry({
        tournamentId: selectedEventId,
        levelInterest:
          sponsorForm.levelInterest === 'not_sure'
            ? null
            : sponsorForm.levelInterest,
        companyName: sponsorForm.companyName.trim(),
        contactName: sponsorForm.contactName.trim(),
        email: sponsorForm.email.trim(),
        phone: sponsorForm.phone.trim(),
        message: sponsorForm.message.trim(),
        userId: user?.uid || null,
      });

      const okMsg = "Thanks. We'll be in touch within 1-2 business days.";
      setSponsorNotice(okMsg);
      setSponsorForm({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        levelInterest: 'not_sure',
        message: '',
      });
      showError('Inquiry Submitted!', okMsg);
    } catch (error) {
      const code = String(error?.code || '');
      if (
        code.includes('failed-precondition') ||
        String(error?.message || '').includes('not taking sponsors')
      ) {
        const closedMsg = 'This event is not taking sponsors right now.';
        setSponsorError(closedMsg);
        showError('Submission Error', closedMsg);
      } else {
        const fallbackMsg =
          'Please try again or email advertising@critcash.com';
        setSponsorError(fallbackMsg);
        showError('Submission Error', fallbackMsg);
      }
    } finally {
      setSponsorSubmitting(false);
    }
  };

  const handleGetStarted = (tier) => {
    setSelectedTier(tier);
    setShowContactModal(true);
  };

  const handleSubmitInquiry = async () => {
    if (!formData.companyName || !formData.email || !formData.contactName) {
      showError('Missing Information', 'Please fill in all required fields.');
      return;
    }

    setSubmitting(true);
    try {
      const functions = getFunctions();
      const submitAdInquiry = httpsCallable(functions, 'submitAdvertiserInquiry');
      await submitAdInquiry({
        ...formData,
        selectedTier: selectedTier?.id || null,
        userId: user?.uid || null,
      });

      setShowContactModal(false);
      setFormData({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        budget: '',
        goals: '',
      });
      
      showError('Inquiry Submitted!', 'We\'ll be in touch within 1-2 business days.');
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      showError('Submission Error', 'Please try again or email advertising@critcash.com');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <Header navigation={navigation} />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#FCD000" />
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <Header navigation={navigation} />
      
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Hero */}
        <View style={styles.heroSection}>
          <Ionicons name="megaphone" size={40} color="#FCD000" />
          <Text style={styles.heroTitle}>Advertise on CritCash</Text>
          <Text style={styles.heroSubtitle}>
            Reach gamers who stream and the viewers who watch them, on a streaming platform with the tools built in.
            High engagement, proven purchase intent.
          </Text>
          
          <TouchableOpacity 
            style={styles.primaryButton}
            onPress={() => handleGetStarted(null)}
          >
            <Text style={styles.primaryButtonText}>Get Started</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.loginButton}
            onPress={() => navigation.navigate('AdvertiserDashboard')}
          >
            <Ionicons name="log-in-outline" size={16} color="#FCD000" style={{ marginRight: 6 }} />
            <Text style={styles.loginButtonText}>Advertiser Login</Text>
          </TouchableOpacity>
        </View>

        {/* Sponsor an event (shown only when at least one sponsorable event is open) */}
        {sponsorableEvents.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Sponsor an event</Text>
            <Text style={styles.sponsorIntroText}>
              {"Tell us which event you'd like to sponsor. We'll connect you with the organizer."}
            </Text>

            {Boolean(sponsorNotice) && (
              <View style={styles.sponsorSuccessBox}>
                <Text style={styles.sponsorSuccessText}>{sponsorNotice}</Text>
              </View>
            )}
            {Boolean(sponsorError) && (
              <View style={styles.sponsorErrorBox}>
                <Text style={styles.sponsorErrorText}>{sponsorError}</Text>
              </View>
            )}

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Event *</Text>
              <View style={styles.sponsorChipRow}>
                {sponsorableEvents.map((ev) => {
                  const active = selectedEventId === ev.tournamentId;
                  return (
                    <TouchableOpacity
                      key={ev.tournamentId}
                      style={[
                        styles.sponsorChip,
                        active && styles.sponsorChipActive,
                      ]}
                      onPress={() => setSelectedEventId(ev.tournamentId)}
                    >
                      <Text
                        style={[
                          styles.sponsorChipText,
                          active && styles.sponsorChipTextActive,
                        ]}
                      >
                        {ev.name}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Sponsorship Level</Text>
              <View style={styles.sponsorChipRow}>
                {EVENT_SPONSOR_LEVEL_OPTIONS.map((opt) => {
                  const active = sponsorForm.levelInterest === opt.key;
                  return (
                    <TouchableOpacity
                      key={opt.key}
                      style={[
                        styles.sponsorChip,
                        active && styles.sponsorChipActive,
                      ]}
                      onPress={() =>
                        setSponsorForm((prev) => ({
                          ...prev,
                          levelInterest: opt.key,
                        }))
                      }
                    >
                      <Text
                        style={[
                          styles.sponsorChipText,
                          active && styles.sponsorChipTextActive,
                        ]}
                      >
                        {opt.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Company Name *</Text>
              <TextInput
                style={styles.formInput}
                placeholder="Your company"
                placeholderTextColor="#666"
                accessibilityLabel="Sponsor Company Name"
                maxLength={100}
                value={sponsorForm.companyName}
                onChangeText={(text) =>
                  setSponsorForm((prev) => ({ ...prev, companyName: text }))
                }
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Contact Name *</Text>
              <TextInput
                style={styles.formInput}
                placeholder="Your name"
                placeholderTextColor="#666"
                accessibilityLabel="Sponsor Contact Name"
                maxLength={100}
                value={sponsorForm.contactName}
                onChangeText={(text) =>
                  setSponsorForm((prev) => ({ ...prev, contactName: text }))
                }
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Email *</Text>
              <TextInput
                style={styles.formInput}
                placeholder="you@company.com"
                placeholderTextColor="#666"
                accessibilityLabel="Sponsor Email"
                keyboardType="email-address"
                autoCapitalize="none"
                maxLength={254}
                value={sponsorForm.email}
                onChangeText={(text) =>
                  setSponsorForm((prev) => ({ ...prev, email: text }))
                }
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Phone (Optional)</Text>
              <TextInput
                style={styles.formInput}
                placeholder="Your phone"
                placeholderTextColor="#666"
                accessibilityLabel="Sponsor Phone"
                keyboardType="phone-pad"
                maxLength={40}
                value={sponsorForm.phone}
                onChangeText={(text) =>
                  setSponsorForm((prev) => ({ ...prev, phone: text }))
                }
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Message (Optional)</Text>
              <TextInput
                style={[styles.formInput, styles.formTextArea]}
                placeholder="Anything you'd like the organizer to know?"
                placeholderTextColor="#666"
                accessibilityLabel="Sponsor Message"
                multiline
                numberOfLines={3}
                maxLength={1000}
                value={sponsorForm.message}
                onChangeText={(text) =>
                  setSponsorForm((prev) => ({ ...prev, message: text }))
                }
              />
            </View>

            <TouchableOpacity
              accessibilityLabel="Submit event sponsor inquiry"
              style={[
                styles.primaryButton,
                { alignSelf: 'flex-start', marginTop: 4 },
                sponsorSubmitting && styles.modalButtonDisabled,
              ]}
              onPress={handleSubmitEventSponsor}
              disabled={sponsorSubmitting}
            >
              {sponsorSubmitting ? (
                <ActivityIndicator size="small" color="#000" />
              ) : (
                <Text style={styles.primaryButtonText}>Sponsor this event</Text>
              )}
            </TouchableOpacity>
          </View>
        )}

        {/* Audience - Single consolidated section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>The Audience</Text>
          
          <View style={styles.audienceGrid}>
            <View style={styles.audienceItem}>
              <Ionicons name="shield-checkmark" size={24} color="#2ECC71" />
              <Text style={styles.audienceLabel}>Streamers</Text>
              <Text style={styles.audienceDesc}>Gamers who go live on any game</Text>
            </View>

            <View style={styles.audienceItem}>
              <Ionicons name="wallet" size={24} color="#3498db" />
              <Text style={styles.audienceLabel}>Paying Users</Text>
              <Text style={styles.audienceDesc}>{payingUsersDescFor(everLaunched, teamEverLaunched)}</Text>
            </View>
            
            <View style={styles.audienceItem}>
              <Ionicons name="game-controller" size={24} color="#9146FF" />
              <Text style={styles.audienceLabel}>FGC Focus</Text>
              <Text style={styles.audienceDesc}>Fighting game community</Text>
            </View>
            
            <View style={styles.audienceItem}>
              <Ionicons name="globe" size={24} color="#FCD000" />
              <Text style={styles.audienceLabel}>US/Canada</Text>
              <Text style={styles.audienceDesc}>North American primary</Text>
            </View>
          </View>
        </View>

        {/* Ad Placements */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Ad Placements</Text>

          {AD_PLACEMENTS.map((placement, idx) => (
            <View key={idx} style={styles.placementCard}>
              <View style={styles.placementHeader}>
                <Ionicons name={placement.iconName} size={22} color="#FCD000" />
                <View style={styles.placementInfo}>
                  <Text style={styles.placementType}>{placement.type}</Text>
                  <Text style={styles.placementFormat}>{placement.format}</Text>
                </View>
              </View>
              <Text style={styles.placementDesc}>{placement.description}</Text>
            </View>
          ))}

          {/* Compliance note */}
          <View style={styles.complianceNote}>
            <Ionicons name="information-circle" size={16} color="#666" />
            <Text style={styles.complianceText}>
              Advertising placements are branding only. Advertisers purchase ad space and
              never influence event outcomes or creator compensation.
            </Text>
          </View>
        </View>

        {/* Partnership Options */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Partnership Options</Text>

          {PARTNERSHIP_TIERS.map((tier) => (
            <View 
              key={tier.id} 
              style={[styles.tierCard, tier.highlight && styles.tierCardHighlight]}
            >
              <View style={styles.tierHeader}>
                <Ionicons name={tier.iconName} size={22} color="#FCD000" />
                <View style={styles.tierInfo}>
                  <Text style={styles.tierName}>{tier.name}</Text>
                  <Text style={styles.tierPrice}>{tier.price}</Text>
                </View>
              </View>
              
              <Text style={styles.tierDesc}>{tier.description}</Text>
              
              <View style={styles.tierFeatures}>
                {tier.features.map((feature, idx) => (
                  <View key={idx} style={styles.featureRow}>
                    <Ionicons name="checkmark" size={14} color="#2ECC71" />
                    <Text style={styles.featureText}>{feature}</Text>
                  </View>
                ))}
              </View>
              
              <TouchableOpacity 
                style={[styles.tierButton, tier.highlight && styles.tierButtonHighlight]}
                onPress={() => handleGetStarted(tier)}
              >
                <Text style={[styles.tierButtonText, tier.highlight && styles.tierButtonTextHighlight]}>
                  {tier.cta}
                </Text>
              </TouchableOpacity>
            </View>
          ))}
        </View>

        {/* Ideal Verticals */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Ideal For</Text>
          <View style={styles.verticalsWrap}>
            {['Gaming Peripherals', 'Energy Drinks', 'Esports Orgs', 'Streaming Services', 'Gaming Furniture', 'Apparel'].map((v, i) => (
              <View key={i} style={styles.verticalTag}>
                <Text style={styles.verticalText}>{v}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* CTA */}
        <View style={styles.ctaSection}>
          <Text style={styles.ctaTitle}>Ready to reach gamers?</Text>
          <TouchableOpacity 
            style={styles.ctaButton}
            onPress={() => handleGetStarted(null)}
          >
            <Text style={styles.ctaButtonText}>Start Conversation</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => Linking.openURL('mailto:advertising@critcash.com')}>
            <Text style={styles.ctaEmail}>advertising@critcash.com</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.bottomPadding} />
      </ScrollView>

      {/* Contact Modal */}
      <Modal
        visible={showContactModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowContactModal(false)}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalContainer}>
            <ScrollView style={styles.modalScroll} contentContainerStyle={styles.modalScrollContent}>
              <View style={styles.modalHeader}>
                <Ionicons name="megaphone" size={36} color="#FCD000" />
                <Text style={styles.modalTitle}>Advertising Inquiry</Text>
                {selectedTier && (
                  <Text style={styles.modalSubtitle}>{selectedTier.name}</Text>
                )}
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Company Name *</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="Your company"
                  placeholderTextColor="#666"
                  value={formData.companyName}
                  onChangeText={(text) => setFormData({ ...formData, companyName: text })}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Contact Name *</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="Your name"
                  placeholderTextColor="#666"
                  value={formData.contactName}
                  onChangeText={(text) => setFormData({ ...formData, contactName: text })}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Email *</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="you@company.com"
                  placeholderTextColor="#666"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  value={formData.email}
                  onChangeText={(text) => setFormData({ ...formData, email: text })}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Phone (Optional)</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="Your phone"
                  placeholderTextColor="#666"
                  keyboardType="phone-pad"
                  value={formData.phone}
                  onChangeText={(text) => setFormData({ ...formData, phone: text })}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Budget Range</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="e.g., $1,000 - $5,000/month"
                  placeholderTextColor="#666"
                  value={formData.budget}
                  onChangeText={(text) => setFormData({ ...formData, budget: text })}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Goals</Text>
                <TextInput
                  style={[styles.formInput, styles.formTextArea]}
                  placeholder="What are you hoping to achieve?"
                  placeholderTextColor="#666"
                  multiline
                  numberOfLines={3}
                  value={formData.goals}
                  onChangeText={(text) => setFormData({ ...formData, goals: text })}
                />
              </View>

              <View style={styles.modalActions}>
                <TouchableOpacity
                  style={styles.modalButtonCancel}
                  onPress={() => setShowContactModal(false)}
                >
                  <Text style={styles.modalButtonCancelText}>Cancel</Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[styles.modalButtonSubmit, submitting && styles.modalButtonDisabled]}
                  onPress={handleSubmitInquiry}
                  disabled={submitting}
                >
                  {submitting ? (
                    <ActivityIndicator size="small" color="#000" />
                  ) : (
                    <Text style={styles.modalButtonSubmitText}>Submit</Text>
                  )}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 80,
  },
  
  // Hero
  heroSection: {
    padding: 24,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
  },
  heroTitle: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#FFF',
    marginTop: 12,
    marginBottom: 8,
    textAlign: 'center',
  },
  heroSubtitle: {
    fontSize: 15,
    color: '#888',
    textAlign: 'center',
    maxWidth: 320,
    lineHeight: 22,
    marginBottom: 20,
  },
  primaryButton: {
    backgroundColor: '#FCD000',
    paddingVertical: 12,
    paddingHorizontal: 28,
    borderRadius: 8,
  },
  primaryButtonText: {
    color: '#000',
    fontSize: 15,
    fontWeight: 'bold',
  },
  loginButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#333',
    backgroundColor: 'transparent',
  },
  loginButtonText: {
    color: '#FCD000',
    fontSize: 14,
    fontWeight: '600',
  },
  
  // Sections
  section: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1A1A',
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 16,
  },
  
  // Audience Grid
  audienceGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  audienceItem: {
    width: '50%',
    paddingRight: 12,
    marginBottom: 16,
  },
  audienceLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#FFF',
    marginTop: 8,
  },
  audienceDesc: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
  
  // Placements
  placementCard: {
    backgroundColor: '#111',
    borderRadius: 10,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#222',
  },
  placementHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  placementInfo: {
    marginLeft: 12,
    flex: 1,
  },
  placementType: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#FFF',
  },
  placementFormat: {
    fontSize: 12,
    color: '#666',
  },
  placementDesc: {
    fontSize: 13,
    color: '#999',
    lineHeight: 18,
  },
  complianceNote: {
    flexDirection: 'row',
    marginTop: 12,
    padding: 12,
    backgroundColor: '#0A0A0A',
    borderRadius: 8,
    gap: 8,
  },
  complianceText: {
    flex: 1,
    fontSize: 11,
    color: '#666',
    lineHeight: 16,
  },
  
  // Tier Cards
  tierCard: {
    backgroundColor: '#111',
    borderRadius: 10,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#222',
  },
  tierCardHighlight: {
    borderColor: '#FCD000',
  },
  tierHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  tierInfo: {
    marginLeft: 12,
    flex: 1,
  },
  tierName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFF',
  },
  tierPrice: {
    fontSize: 13,
    color: '#FCD000',
  },
  tierDesc: {
    fontSize: 13,
    color: '#999',
    marginBottom: 12,
    lineHeight: 18,
  },
  tierFeatures: {
    marginBottom: 12,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    gap: 8,
  },
  featureText: {
    fontSize: 13,
    color: '#CCC',
  },
  tierButton: {
    backgroundColor: '#1A1A1A',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#333',
  },
  tierButtonHighlight: {
    backgroundColor: '#FCD000',
    borderColor: '#FCD000',
  },
  tierButtonText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: 'bold',
  },
  tierButtonTextHighlight: {
    color: '#000',
  },
  
  // Verticals
  verticalsWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  verticalTag: {
    backgroundColor: '#1A1A1A',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 20,
  },
  verticalText: {
    fontSize: 13,
    color: '#CCC',
  },
  
  // CTA
  ctaSection: {
    alignItems: 'center',
    paddingVertical: 32,
    paddingHorizontal: 20,
  },
  ctaTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 16,
  },
  ctaButton: {
    backgroundColor: '#FCD000',
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 8,
    marginBottom: 12,
  },
  ctaButtonText: {
    color: '#000',
    fontSize: 15,
    fontWeight: 'bold',
  },
  ctaEmail: {
    fontSize: 14,
    color: '#FCD000',
  },
  
  bottomPadding: {
    height: 40,
  },
  
  // Modal
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContainer: {
    backgroundColor: '#111',
    borderRadius: 16,
    width: '100%',
    maxWidth: 440,
    maxHeight: '85%',
  },
  modalScroll: {
    maxHeight: '100%',
  },
  modalScrollContent: {
    padding: 20,
  },
  modalHeader: {
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#FFF',
    marginTop: 10,
  },
  modalSubtitle: {
    fontSize: 14,
    color: '#FCD000',
  },
  formGroup: {
    marginBottom: 14,
  },
  formLabel: {
    fontSize: 12,
    color: '#888',
    marginBottom: 6,
    fontWeight: '600',
  },
  formInput: {
    backgroundColor: '#1A1A1A',
    borderRadius: 8,
    padding: 12,
    fontSize: 15,
    color: '#FFF',
    borderWidth: 1,
    borderColor: '#333',
  },
  formTextArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 20,
  },
  modalButtonCancel: {
    flex: 1,
    backgroundColor: '#1A1A1A',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalButtonCancelText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '600',
  },
  modalButtonSubmit: {
    flex: 2,
    backgroundColor: '#FCD000',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalButtonSubmitText: {
    color: '#000',
    fontSize: 14,
    fontWeight: 'bold',
  },
  modalButtonDisabled: {
    backgroundColor: '#666',
  },
  sponsorIntroText: {
    fontSize: 14,
    color: '#AAA',
    lineHeight: 20,
    marginBottom: 16,
  },
  sponsorChipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  sponsorChip: {
    backgroundColor: '#1A1A1A',
    borderWidth: 1,
    borderColor: '#333',
    borderRadius: 18,
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  sponsorChipActive: {
    borderColor: '#FCD000',
    backgroundColor: 'rgba(252, 208, 0, 0.14)',
  },
  sponsorChipText: {
    color: '#CCC',
    fontSize: 13,
    fontWeight: '600',
  },
  sponsorChipTextActive: {
    color: '#FCD000',
    fontWeight: 'bold',
  },
  sponsorSuccessBox: {
    backgroundColor: 'rgba(46, 204, 113, 0.14)',
    borderWidth: 1,
    borderColor: '#2ECC71',
    borderRadius: 8,
    padding: 12,
    marginBottom: 14,
  },
  sponsorSuccessText: {
    color: '#2ECC71',
    fontSize: 13,
    fontWeight: '600',
  },
  sponsorErrorBox: {
    backgroundColor: 'rgba(255, 90, 90, 0.14)',
    borderWidth: 1,
    borderColor: '#ff5a5a',
    borderRadius: 8,
    padding: 12,
    marginBottom: 14,
  },
  sponsorErrorText: {
    color: '#ff9b9b',
    fontSize: 13,
    fontWeight: '600',
  },
});